import { LocationAddressFragment, RetailerNetworkLocationFragment } from '@occc/graphql';

export type PinCoords = { uuid: string; lat: number; lng: number; name?: string | null };

// Module-level cache: keyed by address string, value is the resolved lat/lng.
// Prevents repeated Geocoding API calls for the same address across components and navigations,
// avoiding rate-limit failures (10 req/s per user) when many StoreMapThumbnails mount at once.
// Capped at MAX_CACHE_SIZE to bound memory in long-lived SPA sessions; oldest entry is evicted first.
const MAX_GEOCODE_CACHE_SIZE = 500;
const geocodeCache = new Map<string, { lat: number; lng: number }>();

export const formatAddressForGeocoding = (
  address: LocationAddressFragment | null | undefined,
): string | null => {
  if (!address) return null;
  return [address.street1, address.city, address.state, address.zipCode].filter(Boolean).join(', ');
};

export const fitBoundsToPins = (map: google.maps.Map, pins: PinCoords[], singlePinZoom: number) => {
  if (pins.length === 0) return;
  if (pins.length === 1) {
    map.setCenter({ lat: pins[0].lat, lng: pins[0].lng });
    map.setZoom(singlePinZoom);
    return;
  }
  const bounds = new google.maps.LatLngBounds();
  pins.forEach((pin) => bounds.extend({ lat: pin.lat, lng: pin.lng }));
  map.fitBounds(bounds, 40);
};

export const geocodeAddressString = (
  address: string,
  geocoder: google.maps.Geocoder,
): Promise<google.maps.LatLngLiteral | null> => {
  const cached = geocodeCache.get(address);
  if (cached) return Promise.resolve(cached);

  const geocodePromise = geocoder.geocode({ address });
  if (!geocodePromise) return Promise.resolve(null);

  return geocodePromise
    .then((result) => {
      const pos = result.results[0]?.geometry.location;
      if (!pos) return null;
      const coords = { lat: pos.lat(), lng: pos.lng() };
      if (geocodeCache.size >= MAX_GEOCODE_CACHE_SIZE) {
        geocodeCache.delete(geocodeCache.keys().next().value!);
      }
      geocodeCache.set(address, coords);
      return coords;
    })
    .catch(() => null);
};

export const resolveLocationsToCoords = (
  locations: RetailerNetworkLocationFragment[],
  geocoder: google.maps.Geocoder,
): Promise<PinCoords[]> => {
  const resolved: PinCoords[] = [];

  // Fallback: geocodes addresses when backend-stored geoLocation is missing.
  // retailerNetworkLocationCoordinates is deployed but requires city/state filters —
  // full migration happens when the filter bar is wired up.
  const tasks = locations.map((loc) => {
    const geo = loc.location.address?.geoLocation;
    if (geo) {
      resolved.push({
        uuid: loc.uuid,
        lat: geo.latitude,
        lng: geo.longitude,
        name: loc.location.name,
      });
      return Promise.resolve();
    }

    const address = formatAddressForGeocoding(loc.location.address);
    if (!address) return Promise.resolve();

    return geocodeAddressString(address, geocoder).then((coords) => {
      if (coords) resolved.push({ uuid: loc.uuid, ...coords, name: loc.location.name });
    });
  });

  return Promise.all(tasks).then(() => resolved);
};
